import clsx from 'clsx';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { VscClose } from 'react-icons/vsc';
import { Button } from '../buttons/Button';
import { Loader } from '../loader/Loader';
import { LoaderWrapper } from '../loader/LoaderWrapper';

type MaxWidthSize = 'normal' | 'medium';
export interface ModalBasicProps {
  hasHeaderCloseBtn?: boolean;
  isVisible: boolean;
  imageHeading?: {
    optimized?: any;
    src?: string;
    className?: string;
  };
  title?: string;
  approveBtnLabel?: string;
  isApproveBtnDisabled?: boolean;
  secondaryBtnLabel?: string;
  children?: React.ReactNode;
  hasModalFooter?: boolean;
  isLoading?: boolean;
  contentClassName?: string;
  maxWidthSize?: MaxWidthSize;
  onClose: () => void;
  onSecondaryBtnClick?: () => void | undefined;
  onApprove?: () => void;
}

const stylesMaxWidthConfig = {
  normal: 'md:basis-[528px]',
  medium: 'md:basis-[808px]',
};

function useComponentState(props: ModalBasicProps) {
  const { isVisible = false, hasModalFooter = true, maxWidthSize = 'normal', children, isLoading = false } = props;

  useEffect(() => {
    const bodyEl = document.body;

    if (isVisible) {
      bodyEl.classList.add('modal-open');
    } else {
      bodyEl.classList.remove('modal-open');
    }
  }, [isVisible]);

  const modalWrapperCn = clsx(
    'overflow-y-auto overflow-x-hidden fixed inset-0 z-overlay bg-white md:justify-center md:pt-20 md:pb-4 md:items-start md:bg-black/60 transition-colors',
    isVisible ? 'visible md:flex' : 'hidden',
  );
  const modalContentCn = clsx(
    'relative min-h-full flex flex-col p-4 md:min-h-0 md:block basis-[528px] md:h-auto md:p-8 md:rounded-lg md:bg-white',
    stylesMaxWidthConfig[maxWidthSize],
  );
  const modalFooterCn = clsx(
    'flex flex-col items-center space-y-4 mt-6 md:flex-row md:justify-end md:mt-8 md:space-y-0',
    !hasModalFooter && 'hidden',
    {
      'flex-1 justify-end': !children,
    },
  );

  return {
    modalWrapperCn,
    modalContentCn,
    modalFooterCn,
    ...props,
    isLoading,
  };
}

export const ModalBasic: React.FC<ModalBasicProps> = (props) => {
  const {
    hasHeaderCloseBtn = true,
    imageHeading,
    title,
    approveBtnLabel = '',
    secondaryBtnLabel = '',
    isApproveBtnDisabled = false,
    modalWrapperCn,
    modalContentCn,
    modalFooterCn,
    children,
    onClose,
    onSecondaryBtnClick = undefined,
    onApprove = () => {},
    isLoading,
    contentClassName,
  } = useComponentState(props);
  const { t } = useTranslation();

  const handleSecondaryBtnClick = useMemo(() => {
    return onSecondaryBtnClick ? onSecondaryBtnClick : onClose;
  }, [onSecondaryBtnClick, onClose]);

  return (
    <div tabIndex={-1} className={modalWrapperCn}>
      <div className={modalContentCn}>
        {hasHeaderCloseBtn && (
          <button className="absolute inline-flex items-center p-1 top-3 right-3 md:top-4 md:right-4" onClick={onClose}>
            <VscClose size={26} />
          </button>
        )}
        {imageHeading && (
          <>
            {imageHeading.optimized && (
              <GatsbyImage
                // @ts-ignore
                image={getImage(imageHeading.optimized)}
                alt="icon"
                className={clsx(imageHeading.className || 'w-20 h-20')}
              />
            )}
            {imageHeading.src && (
              <img src={imageHeading.src} alt="" className={clsx(imageHeading.className || 'w-20 h-20')} />
            )}
          </>
        )}
        {title && <h6 className="mt-2 mr-8 md:mt-4 md:text-xl md:mr-4">{t(title)}</h6>}
        {children && <div className={clsx('flex-1 mt-4', contentClassName)}>{children}</div>}
        <div className={modalFooterCn}>
          {/*{t(approveBtnLabel) === 'Оплатити' || t(approveBtnLabel) === 'Pay' ? (*/}
          {/*    approveBtnLabel && <Button*/}
          {/*    as="button"*/}
          {/*    type="button"*/}
          {/*    size="small"*/}
          {/*    className="whitespace-nowrap md:order-2 md:ml-4"*/}
          {/*    disabled={isApproveBtnDisabled}*/}
          {/*  >*/}
          {/*    {t(approveBtnLabel)}*/}
          {/*  </Button>*/}
          {/*) : (*/}
          {/*  approveBtnLabel && <Button*/}
          {/*    as="button"*/}
          {/*    type="button"*/}
          {/*    size="small"*/}
          {/*    onClick={onApprove}*/}
          {/*    className="whitespace-nowrap md:order-2 md:ml-4"*/}
          {/*    disabled={isApproveBtnDisabled}*/}
          {/*  >*/}
          {/*    {t(approveBtnLabel)}*/}
          {/*  </Button>*/}
          {/*)}*/}
          {approveBtnLabel && (<Button
            as="button"
            type="button"
            size="small"
            onClick={onApprove}
            className="whitespace-nowrap md:order-2 md:ml-4"
            disabled={isApproveBtnDisabled}
          >
            {t(approveBtnLabel)}
          </Button>
          )}
          {secondaryBtnLabel && (
            <Button
              as="button"
              type="button"
              variant="secondary"
              size="small"
              className="whitespace-nowrap"
              onClick={handleSecondaryBtnClick}
            >
              {t(secondaryBtnLabel)}
            </Button>
          )}
        </div>
        {isLoading && (
          <LoaderWrapper className="md:rounded-lg">
            <Loader className="h-20" />
          </LoaderWrapper>
        )}
      </div>
    </div>
  );
};
